import ReactDOM from 'react-dom';

class WebSocketManager {
  // ✅ NEW: petite queue pour batcher les messages reçus
  messageQueue = [];

  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.baseDelay = 1000;
    this.subscribers = new Set();
    this.jobSubscribers = new Map(); // jobId -> Set of callbacks
    this.connectionTimeout = null;
    this.reconnectTimeout = null;
    this.intentionalDisconnect = false;
    this.lastMessageTime = null;
    this.currentToken = null;
    this.isConnecting = false; // ✅ ADD: Prevent multiple connection attempts
    this.connectionQueue = []; // ✅ ADD: Queue connection requests
    this.processedMessages = new Set(); // ✅ ADD: Track processed messages to prevent duplicates
    this.rpaWs = null; // ✅ ADD: RPA WebSocket connection
    this.rpaMessageHandler = null; // ✅ ADD: RPA message handler callback
    this.rpaFlowId = null; // ✅ ADD: Current RPA flow ID
    window.addEventListener('online', () => {
      if (this.intentionalDisconnect || this.isConnected ||
          (!this.subscribers.size && !this.jobSubscribers.size)) return;
      clearTimeout(this.reconnectTimeout);
      this.reconnectAttempts = 0;
      this.connect(localStorage.getItem('access_token'));
    });
    
    // Flush toutes les ~60 ms (≈ 16 fps)
    setInterval(() => {
      if (!this.isConnected || this.messageQueue.length === 0) return;
      const batch = this.messageQueue.splice(0);
      if (ReactDOM.unstable_batchedUpdates) {
        ReactDOM.unstable_batchedUpdates(() => {
          batch.forEach((msg) => this.dispatchMessage(msg));
        });
      } else {
        batch.forEach((msg) => this.dispatchMessage(msg));
      }
    }, 60);
  }

  subscribe(callback) {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  subscribeToJob(jobId, callback) {
    if (!this.jobSubscribers.has(jobId)) {
      this.jobSubscribers.set(jobId, new Set());
    }
    this.jobSubscribers.get(jobId).add(callback);

    // If connected, send subscription message
    if (this.isConnected && this.ws) {
      this.sendMessage({
        type: 'subscribe_job',
        job_id: jobId
      });
    }

    return () => {
      const jobCallbacks = this.jobSubscribers.get(jobId);
      if (jobCallbacks) {
        jobCallbacks.delete(callback);
        if (jobCallbacks.size === 0) {
          this.jobSubscribers.delete(jobId);
        }
      }
    };
  }

  // ✅ FIX: Prevent multiple concurrent connections
  async connect(token) {
    // ✅ FIX: Always get latest token from localStorage (api.js may have refreshed it)
    const latestToken = localStorage.getItem('access_token') || token;
    if (!latestToken) {
      console.error('❌ No token available for WebSocket connection');
      return;
    }

    this.intentionalDisconnect = false;

    // ✅ FIX: If already connected with same token, do nothing
    if (this.isConnected && this.currentToken === latestToken) {
      console.log('🔌 WebSocket already connected with same token');
      return;
    }

    // ✅ FIX: If already connecting, queue this request
    if (this.isConnecting) {
      console.log('🔌 WebSocket connection in progress, queuing request');
      return new Promise((resolve) => {
        this.connectionQueue.push(resolve);
      });
    }

    this.isConnecting = true;
    this.isConnected = false;

    try {
      // Use latest token from localStorage (api.js handles refresh centrally)
      const validToken = localStorage.getItem('access_token') || latestToken;
      if (!validToken) {
        console.error('❌ No valid token available, cannot connect');
        this.isConnecting = false;
        return;
      }

      this.currentToken = validToken;

      // ✅ FIX: Only disconnect if we're changing tokens
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.close(1000, 'Reconnecting');
      }

      let wsUrl;
      
      if (process.env.REACT_APP_WS_URL) {
        wsUrl = process.env.REACT_APP_WS_URL;
      } else {
        const protocol = process.env.REACT_APP_WS_PROTOCOL || 
                        (window.location.protocol === 'https:' ? 'wss' : 'ws');
        const host = process.env.REACT_APP_WS_HOST || 
             (process.env.NODE_ENV === 'development' ? 'localhost:8000' : window.location.host);
        wsUrl = `${protocol}://${host}/api/v1/ws`;
      }
      
      console.log('🔌 Connecting to WebSocket:', wsUrl);

      const socket = new WebSocket(wsUrl);
      this.ws = socket;

      this.connectionTimeout = setTimeout(() => {
        if (this.ws === socket && !this.isConnected) {
          console.error('❌ WebSocket connection timeout');
          socket.close();
        }
      }, 10000);

      socket.onopen = () => {
        if (this.ws !== socket) return;
        socket.send(JSON.stringify({ type: 'authenticate', token: validToken }));
      };

      socket.onmessage = (event) => {
        if (this.ws !== socket) return;
        this.handleMessage(event);
      };

      socket.onclose = (event) => {
        if (this.ws !== socket) return;
        clearTimeout(this.connectionTimeout);
        this.ws = null;
        this.isConnected = false;
        this.isConnecting = false;

        // Clear processed messages on disconnect to allow reconnection
        this.processedMessages.clear();

        // 🔍 ENHANCED MONITORING: Detailed WebSocket close event logging
        console.log('🔌 WebSocket closed:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          timestamp: new Date().toISOString(),
          reconnectAttempts: this.reconnectAttempts,
          maxReconnectAttempts: this.maxReconnectAttempts,
          hasSubscribers: this.subscribers.size > 0 || this.jobSubscribers.size > 0,
          localStorageTokens: {
            accessToken: localStorage.getItem('access_token') ? 'present' : 'missing',
            refreshToken: localStorage.getItem('refresh_token') ? 'present' : 'missing',
            user: localStorage.getItem('user') ? 'present' : 'missing'
          }
        });

        // ✅ FIX: Only reconnect if we have subscribers and haven't exceeded max attempts
        const hasSubscribers = this.subscribers.size > 0 || this.jobSubscribers.size > 0;
        
        if (!this.intentionalDisconnect && hasSubscribers && this.reconnectAttempts < this.maxReconnectAttempts) {
          if (event.code === 4001) {
            console.error('❌ WebSocket: Authentication failed');
            const delay = this.baseDelay * Math.pow(2, this.reconnectAttempts);
            console.log(`🔄 Attempting to refresh token and reconnect in ${delay}ms...`);
            this.reconnectTimeout = setTimeout(async () => {
              this.reconnectAttempts++;
              try {
                const { authApi } = await import('./api');
                await authApi.refresh();
                if (!this.intentionalDisconnect) await this.connect(localStorage.getItem('access_token'));
              } catch (error) {
                console.error('WebSocket authentication refresh failed:', error);
              }
            }, delay);
          } else {
            const delay = this.baseDelay * Math.pow(2, this.reconnectAttempts);
            console.log(`🔄 Reconnecting in ${delay}ms...`);
            this.reconnectTimeout = setTimeout(async () => {
              this.reconnectAttempts++;
              const newToken = localStorage.getItem('access_token');
              if (newToken) {
                await this.connect(newToken);
              }
            }, delay);
          }
        } else if (!hasSubscribers) {
          console.log('🔌 No subscribers, not reconnecting');
        }
      };

      socket.onerror = (error) => {
        if (this.ws !== socket) return;
        clearTimeout(this.connectionTimeout);
        this.isConnecting = false;
        console.error('❌ WebSocket error:', error);
      };

    } catch (err) {
      this.isConnecting = false;
      console.error('❌ Failed to create WebSocket connection:', err);
    }
  }

  handleMessage(event) {
    try {
      const message = JSON.parse(event.data);
      
      // ✅ FIX: Don't queue connection test messages
      if (message.type === 'connection_test') {
        clearTimeout(this.connectionTimeout);
        this.isConnected = true;
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.connectionQueue.splice(0).forEach(resolve => resolve());
        for (const jobId of this.jobSubscribers.keys()) {
          this.sendMessage({ type: 'subscribe_job', job_id: jobId });
        }
        this.dispatchMessage(message);
        return;
      }
      
      // ✅ ADD: Create message fingerprint for deduplication
      const messageFingerprint = this.createMessageFingerprint(message);
      
      // ✅ ADD: Skip duplicate messages
      if (this.processedMessages.has(messageFingerprint)) {
        console.log('🔄 Skipping duplicate WebSocket message:', messageFingerprint);
        return;
      }
      
      // ✅ ADD: Mark message as processed
      this.processedMessages.add(messageFingerprint);
      
      // ✅ ADD: Clean up old fingerprints to prevent memory leaks (keep last 1000)
      if (this.processedMessages.size > 1000) {
        const oldFingerprints = Array.from(this.processedMessages).slice(0, 500);
        oldFingerprints.forEach(fp => this.processedMessages.delete(fp));
      }
      
      // Limit queue size for screencast frames to prevent memory issues
      if (message.event_type === "screencast_frame" && this.messageQueue.length > 200) {
        this.messageQueue.shift();
      }
      this.messageQueue.push(message);
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }

  // ✅ ADD: Create a fingerprint for message deduplication
  createMessageFingerprint(message) {
    // Create a unique fingerprint based on message content and timestamp
    const key = `${message.event_type || message.type}_${message.entity_id}_${message.timestamp}_${JSON.stringify(message.payload || message.data || {})}`;
    return key;
  }

  // 🔒 Conserve le comportement existant pour notifier les abonnés
  dispatchMessage(message) {
    // ✅ FIX: Only log non-connection-test messages
    if (message.type !== 'connection_test') {
      console.log('🔌 WebSocket RAW message received:', message);
    }
    
    // Enhanced logging for need_created messages
    if (message.event_type === 'need_created') {
      console.log('🎯 NEED_CREATED WebSocket message:', {
        event_type: message.event_type,
        entity_id: message.entity_id,
        payload: message.payload,
        hasPayload: !!message.payload,
        payloadKeys: message.payload ? Object.keys(message.payload) : []
      });
    }
    
    // Notify all subscribers
    this.subscribers.forEach(callback => {
      try {
        callback(message);
      } catch (error) {
        console.error('Error in WebSocket message handler:', error);
      }
    });

    // Broadcast to job-specific subscribers
    if (message.entity_id) {
      const jobCallbacks = this.jobSubscribers.get(message.entity_id);
      if (jobCallbacks) {
        jobCallbacks.forEach(callback => {
          try {
            callback(message);
          } catch (error) {
            console.error('❌ Error in job subscriber callback:', error);
          }
        });
      }
    }
  }

  sendMessage(message) {
    if (this.ws && this.isConnected) {
      this.ws.send(JSON.stringify(message));
    }
  }

  disconnect() {
    this.intentionalDisconnect = true;
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close(1000, 'Disconnecting');
      this.ws = null;
    }
    
    this.isConnected = false;
    this.isConnecting = false;
    this.currentToken = null; // ✅ ADD: Clear current token
    this.messageQueue = [];
    this.processedMessages.clear();
    
    // Clear connection queue
    this.connectionQueue = [];
  }

  getConnectionStatus() {
    return this.isConnected;
  }

  // ✅ ADD: Connect to RPA live stream
  async connectToRpaStream(flowId, token, onMessage) {
    if (!flowId || !token) {
      console.error('❌ RPA Stream: Missing flowId or token');
      throw new Error('Missing flowId or token');
    }

    // If already connected to the same flow, return existing connection
    if (this.rpaWs && this.rpaWs.readyState === WebSocket.OPEN && this.rpaFlowId === flowId) {
      console.log('🔌 RPA Stream: Already connected to flow', flowId);
      return this.rpaWs;
    }

    // Disconnect existing RPA connection if different flow
    if (this.rpaWs && this.rpaFlowId !== flowId) {
      console.log('🔌 RPA Stream: Disconnecting from previous flow', this.rpaFlowId);
      this.disconnectRpaStream();
    }

    try {
      // Use latest token from localStorage (api.js handles refresh centrally)
      const validToken = localStorage.getItem('access_token') || token;
      if (!validToken) {
        throw new Error('No valid token available');
      }

      this.rpaFlowId = flowId;
      this.rpaMessageHandler = onMessage;

      // Build WebSocket URL for RPA live stream
      let wsUrl;
      if (process.env.REACT_APP_WS_URL) {
        const baseUrl = process.env.REACT_APP_WS_URL.replace(/\/ws.*$/, '');
        wsUrl = `${baseUrl}/api/v1/rpa/live/${flowId}`;
      } else {
        const protocol = process.env.REACT_APP_WS_PROTOCOL || 
                        (window.location.protocol === 'https:' ? 'wss' : 'ws');
        const host = process.env.REACT_APP_WS_HOST || 
                     (process.env.NODE_ENV === 'development' ? 'localhost:8000' : window.location.host);
        wsUrl = `${protocol}://${host}/api/v1/rpa/live/${flowId}`;
      }

      console.log('🔌 RPA Stream: Connecting to', wsUrl);

      this.rpaWs = new WebSocket(wsUrl);

      // Wait for connection to open
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          if (this.rpaWs && this.rpaWs.readyState === WebSocket.OPEN) {
            resolve(this.rpaWs);
          } else {
            reject(new Error('RPA Stream connection timeout'));
          }
        }, 10000);

        this.rpaWs.onopen = () => {
          clearTimeout(timeout);
          this.rpaWs.send(JSON.stringify({ type: 'authenticate', token: validToken }));
          console.log('✅ RPA Stream: Connected successfully for flow', flowId);
          resolve(this.rpaWs);
        };

        this.rpaWs.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (this.rpaMessageHandler) {
              this.rpaMessageHandler(data);
            }
          } catch (error) {
            console.error('❌ RPA Stream: Error parsing message:', error);
          }
        };

        this.rpaWs.onclose = (event) => {
          console.log('🔌 RPA Stream: Connection closed for flow', flowId, {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean
          });
          this.rpaWs = null;
          this.rpaFlowId = null;
          this.rpaMessageHandler = null;
        };

        this.rpaWs.onerror = (error) => {
          clearTimeout(timeout);
          console.error('❌ RPA Stream: WebSocket error:', error);
          reject(new Error('RPA Stream WebSocket error', { cause: error }));
        };
      });
    } catch (error) {
      console.error('❌ RPA Stream: Failed to connect:', error);
      this.rpaWs = null;
      this.rpaFlowId = null;
      this.rpaMessageHandler = null;
      throw error;
    }
  }

  // ✅ ADD: Disconnect from RPA live stream
  disconnectRpaStream() {
    if (this.rpaWs) {
      console.log('🔌 RPA Stream: Disconnecting from flow', this.rpaFlowId);
      this.rpaWs.close(1000, 'Disconnecting');
      this.rpaWs = null;
      this.rpaFlowId = null;
      this.rpaMessageHandler = null;
    }
  }

}

// Export singleton instance
const websocketManager = new WebSocketManager();
export default websocketManager;
